import { useEffect, useId, useRef, useState } from "react";
import type { ParentCandidate, ParentSearch } from "@mk/core";
import { api } from "../api";

export function ParentPicker({
  project,
  revision,
}: {
  project: string;
  revision: unknown;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ParentCandidate | null>(null);
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState(1);
  const [active, setActive] = useState(0);
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [result, setResult] = useState<
    ParentSearch & { query: string; project: string }
  >({
    items: [],
    total: 0,
    next_offset: null,
    query: "",
    project,
  });
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        let offset = 0;
        const items: ParentCandidate[] = [];
        let page: ParentSearch;
        for (let index = 0; index < pages; index++) {
          const params = new URLSearchParams({
            q: query,
            offset: String(offset),
            limit: "20",
          });
          page = await api<ParentSearch>(
            `/v1/projects/${project}/parent-candidates?${params}`,
          );
          if (cancelled) return;
          items.push(...page.items);
          if (page.next_offset === null) break;
          offset = page.next_offset;
        }
        setResult({ ...page!, items, query, project });
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [project, query, pages, revision, retry]);
  const items =
    result.query === query && result.project === project && !error
      ? result.items
      : [];
  const activeIndex = Math.min(active, items.length - 1);
  const activeId =
    open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined;
  useEffect(() => {
    if (activeId)
      document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
  }, [activeId]);
  function choose(task: ParentCandidate) {
    setSelected(task);
    setQuery(task.id);
    setPages(1);
    setActive(0);
    setOpen(false);
    input.current?.focus();
  }
  return (
    <div
      className="parent-picker"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
          input.current?.focus();
        }
      }}
    >
      <label htmlFor={id}>Parent task</label>
      <input type="hidden" name="parent" value={selected?.id ?? ""} />
      <input
        ref={input}
        id={id}
        role="combobox"
        autoComplete="off"
        maxLength={300}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={activeId}
        aria-describedby={`${id}-selection`}
        placeholder="Search ID, title or tags…"
        value={query}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setPages(1);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            setActive(
              !open
                ? e.key === "ArrowDown"
                  ? 0
                  : Math.max(0, items.length - 1)
                : Math.max(
                    0,
                    Math.min(
                      items.length - 1,
                      activeIndex + (e.key === "ArrowDown" ? 1 : -1),
                    ),
                  ),
            );
          } else if (e.key === "Enter" && open) {
            e.preventDefault();
            if (items[activeIndex]) choose(items[activeIndex]);
          }
        }}
      />
      <div className="parent-selection">
        <span id={`${id}-selection`}>
          {selected
            ? `${selected.id} · ${selected.title}`
            : "No parent selected"}
        </span>
        <button
          className="text-button"
          type="button"
          onClick={() => {
            setSelected(null);
            setQuery("");
            setPages(1);
            setActive(0);
            setOpen(false);
            input.current?.focus();
          }}
        >
          No parent
        </button>
      </div>
      {open && (
        <div className="parent-results">
          <ul
            id={`${id}-list`}
            role="listbox"
            aria-label="Parent tasks"
            tabIndex={-1}
          >
            {items.map((task, index) => (
              <li
                key={task.id}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={selected?.id === task.id}
                className={index === activeIndex ? "highlighted" : ""}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(task)}
              >
                <strong>{task.id}</strong> <span>{task.title}</span>
                {task.tags.length > 0 && (
                  <small>{task.tags.map((tag) => `#${tag}`).join(" ")}</small>
                )}
              </li>
            ))}
          </ul>
          <div className="parent-status" role="status">
            {error ||
              (loading || result.query !== query
                ? "Searching…"
                : items.length === 0
                  ? "No matching tasks"
                  : `${items.length} of ${result.total} tasks`)}
          </div>
          {error ? (
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setRetry((n) => n + 1);
                input.current?.focus();
              }}
            >
              Retry parent search
            </button>
          ) : (
            result.next_offset !== null &&
            items.length > 0 && (
              <button
                className="text-button"
                type="button"
                disabled={loading}
                onClick={() => {
                  setPages((n) => n + 1);
                  input.current?.focus();
                }}
              >
                Show more parents
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
